import React, { useState, useEffect, useContext } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Layout, Menu, Avatar, Typography, Badge, Button, Popover, Divider } from "antd";

import { useAppContext } from "@/context/appContext";
import logoIcon from "@/style/images/logo-icon.png";
import i18n from "../../../src/i18n";
import photo from "@/style/images/avatar.webp";
import { useSelector } from "react-redux";

import { modulesConfig } from "@/config/modulesConfig";
import { useConfig } from "@/context/ConfigContext";

import { useCrudDirect } from "@/components/pagesActuales/useCrudDirect";
import { request } from "@/request";

import {
  DashboardOutlined,BellOutlined,TagsOutlined,ShoppingOutlined,FolderOpenOutlined,WalletOutlined,MenuUnfoldOutlined,MenuFoldOutlined,UserOutlined,DatabaseOutlined,
  BgColorsOutlined,AuditOutlined,PercentageOutlined,CopyrightOutlined,LogoutOutlined,TeamOutlined,CreditCardOutlined,ShoppingCartOutlined,ShopOutlined,SettingOutlined,
  FileSyncOutlined,AppstoreOutlined,SolutionOutlined,BarChartOutlined,
} from "@ant-design/icons";

import { UserContext } from "../../context/UserContext";

const { Sider } = Layout;
const { Text, Title } = Typography;

export default function Navigation() {
  const history = useHistory();
  const location = useLocation();

  const msg = useContext(UserContext);

  const { state: stateApp, appContextAction } = useAppContext();
  const { isNavMenuClose } = stateApp;
  const { navMenu } = appContextAction;

  // =========================================================
  // CONFIGURACION VISUAL
  // =========================================================
  const { config, updateGlobalConfig } = useConfig();

  const { items: dataConfig } = useCrudDirect("configuracion");

  // =========================================================
  // TENANT ACTUAL
  // =========================================================
  const tenant = useSelector((state) => state.auth?.current?.tenant);

  // =========================================================
  // PERFIL DE MODULOS
  // =========================================================
  const [perfilConfig, setPerfilConfig] = useState(null);

  // =========================================================
  // USUARIO ACTUAL
  // =========================================================
  const [currentUser, setCurrentUser] = useState({ name: "Cargando...", tenant: "Cargando..." });

  // =========================================================
  // CARGAR CONFIGURACION VISUAL
  // =========================================================
  useEffect(() => {
    if (dataConfig && dataConfig.length > 0) {
      const nuevaConfig = dataConfig[0];

      if (!config || JSON.stringify(config) !== JSON.stringify(nuevaConfig)) {
        updateGlobalConfig(nuevaConfig);
      }
    }

    // No agregamos config/updateGlobalConfig para
    // mantener exactamente el comportamiento existente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataConfig]);

  // =========================================================
  // IDIOMA
  // =========================================================
  useEffect(() => {
    i18n.changeLanguage(msg.language);
  }, [msg.language]);

  // =========================================================
  // LEER USUARIO DESDE LOCALSTORAGE
  // =========================================================
  useEffect(() => {
    try {
      const authDataString = localStorage.getItem("auth");

      if (!authDataString) {
        return;
      }

      const authData = JSON.parse(authDataString);

      const userObj = authData?.user || authData?.data || authData;

      setCurrentUser({ name: userObj?.name || "Usuario Desconocido", tenant: userObj?.tenant || "Empresa Desconocida" });
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    }
  }, []);

  // =========================================================
  // LEER PERFIL DESDE LA BASE DE DATOS
  //
  // NO USAMOS useCrudDirect('perfil')
  // porque Redux tiene un solo crud.list y ya estamos
  // usando useCrudDirect('configuracion').
  //
  // Esta consulta SOLO lee:
  //
  // ../clienteXX/api/v1/perfil
  // =========================================================
  useEffect(() => {
    let activo = true;

    const cargarPerfil = async () => {
      if (!tenant) {
        return;
      }

      try {
        console.log("[Navigation] Consultando perfil para tenant:", tenant);

        const data = await request.list({ entity: `../${tenant}/api/v1/perfil`, options: { page: 1, limit: 10, offset: 0, pais: 1 } });

        console.log("[Navigation] Respuesta de perfil:", data);

        if (!activo) {
          return;
        }

        /*
         * Tu crud.list actual recibe:
         *
         * data.result
         *
         * que normalmente es el arreglo de registros.
         *
         * Por seguridad soportamos también:
         * data.result.items
         */
        let perfiles = [];

        if (Array.isArray(data?.result)) {
          perfiles = data.result;
        } else if (Array.isArray(data?.result?.items)) {
          perfiles = data.result.items;
        }

        if (data?.success === true && perfiles.length > 0) {
          const perfil = perfiles[0];

          console.log("[Navigation] Perfil encontrado:", perfil);

          setPerfilConfig(perfil);
        } else {
          console.warn("[Navigation] No se encontró perfil para:", tenant);

          /*
           * Al dejar null:
           *
           * menuConfig = modulesConfig
           *
           * Por tanto el sistema sigue funcionando
           * con todos los módulos habilitados según
           * modulesConfig.
           */
          setPerfilConfig(null);
        }
      } catch (error) {
        console.error("[Navigation] Error leyendo perfil:", error);

        if (activo) {
          setPerfilConfig(null);
        }
      }
    };

    cargarPerfil();

    return () => {
      activo = false;
    };
  }, [tenant]);

  // =========================================================
  // CONVERTIR LA FILA "perfil" DE BD
  // A LA ESTRUCTURA EXISTENTE DEL MENU
  // =========================================================
  const menuConfig = perfilConfig
    ? {
        // ===================================================
        // DASHBOARD
        // ===================================================
        dashboard: Boolean(perfilConfig.dashboard),

        // ===================================================
        // CLIENTES
        // ===================================================
        clientes: {
          menuCompleto: Boolean(perfilConfig.clientes),

          maestroPacientes: Boolean(perfilConfig.mcliente),

          doctores: Boolean(perfilConfig.vendedor),

          /*
           * No existe una columna "empresas"
           * en perfil.
           *
           * Se conserva habilitada como en la
           * configuración original.
           */
          empresas: true,

          sectores: Boolean(perfilConfig.sector),

          transacciones: Boolean(perfilConfig.tcliente),

          tiposOperacion: Boolean(perfilConfig.topcliente),
        },

        // ===================================================
        // PROVEEDORES
        // ===================================================
        proveedores: {
          menuCompleto: Boolean(perfilConfig.proveedor),

          maestroProveedor: Boolean(perfilConfig.mproveedor),

          transacciones: Boolean(perfilConfig.tproveedor),

          tiposOperacion: Boolean(perfilConfig.topproveedor),
        },

        // ===================================================
        // PRODUCTOS
        // ===================================================
        productos: {
          menuCompleto: Boolean(perfilConfig.producto),

          maestroProducto: Boolean(perfilConfig.mproducto),

          transacciones: Boolean(perfilConfig.tproducto),

          tiposOperacion: Boolean(perfilConfig.topproducto),

          almacenes: Boolean(perfilConfig.almacen),

          presentacion: Boolean(perfilConfig.presentacion),

          marcas: Boolean(perfilConfig.marca),

          categorias: Boolean(perfilConfig.categoria),

          impuestos: Boolean(perfilConfig.impuesto),
        },

        // ===================================================
        // MODULOS PRINCIPALES
        // ===================================================
        facturar: Boolean(perfilConfig.factura),

        cotizar: Boolean(perfilConfig.cotiza),

        compras: Boolean(perfilConfig.compra),

        reportes: Boolean(perfilConfig.reporte),

        // ===================================================
        // CONFIGURACION
        // ===================================================
        configuracion: {
          menuCompleto: Boolean(perfilConfig.config),

          general: Boolean(perfilConfig.genconfig),

          roles: Boolean(perfilConfig.rolconfig),
        },

        // ===================================================
        // IA
        // ===================================================
        ai: {
          menuCompleto: Boolean(perfilConfig.ia),

          general: Boolean(perfilConfig.genia),

          roles: Boolean(perfilConfig.rolia),
        },
      }
    : modulesConfig;

  // =========================================================
  // TEMA
  // =========================================================
  const currentTheme = config?.theme === "black" || config?.theme === "dark" ? "dark" : "light";

  // =========================================================
  // COLORES
  // =========================================================
  const textColor = currentTheme === "dark" ? "rgba(255, 255, 255, 0.85)" : "inherit";

  const textSecondaryColor = currentTheme === "dark" ? "rgba(255, 255, 255, 0.45)" : "inherit";

  const borderColor = currentTheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(140, 140, 140, 0.2)";

  // =========================================================
  // ESTILO TEXTO
  // =========================================================
  const textStyle = { whiteSpace: "nowrap", overflow: isNavMenuClose ? "visible" : "hidden", textOverflow: isNavMenuClose ? "unset" : "ellipsis", display: "inline-block", width: isNavMenuClose ? "auto" : "100%", verticalAlign: "middle" };

  // =========================================================
  // ESTILO ICONOS
  // =========================================================
  const getIconStyle = (color, isEnabled) => ({ fontSize: "18px", color: isEnabled ? color : currentTheme === "dark" ? "#595959" : "#bfbfbf", transition: "transform 0.3s ease" });

  // =========================================================
  // MENU
  // =========================================================
  const menuItems = [
    // =======================================================
    // DASHBOARD
    // =======================================================
    {
      key: "Dashboard",

      disabled: !menuConfig.dashboard,

      icon: <DashboardOutlined style={getIconStyle("#1890ff", menuConfig.dashboard)} />,

      label: (
        <>
          {menuConfig.dashboard && <Link to="/dashboard" />}

          <span style={textStyle}>{i18n.t("dashboard")}</span>
        </>
      ),
    },

    // =======================================================
    // CLIENTES
    // =======================================================
    {
      key: "AdminCustomer",

      disabled: !menuConfig.clientes.menuCompleto,

      icon: <UserOutlined style={getIconStyle("#eb2f96", menuConfig.clientes.menuCompleto)} />,

      label: <span style={textStyle}>Clientes</span>,

      children: [
        {
          key: "CustomerRegister",

          disabled: !menuConfig.clientes.maestroPacientes,

          icon: <WalletOutlined style={getIconStyle("#722ed1", menuConfig.clientes.maestroPacientes)} />,

          label: (
            <>
              {menuConfig.clientes.maestroPacientes && <Link to="/customer" />}

              <span style={textStyle}>Maestro Cliente</span>
            </>
          ),
        },

        {
          key: "VendedorModulo",

          disabled: !menuConfig.clientes.doctores,

          icon: <SolutionOutlined style={getIconStyle("#13c2c2", menuConfig.clientes.doctores)} />,

          label: (
            <>
              {menuConfig.clientes.doctores && <Link to="/vendedores" />}

              <span style={textStyle}>Doctores</span>
            </>
          ),
        },

        {
          key: "Sectores",

          disabled: !menuConfig.clientes.sectores,

          icon: <AppstoreOutlined style={getIconStyle("#eb2f96", menuConfig.clientes.sectores)} />,

          label: (
            <>
              {menuConfig.clientes.sectores && <Link to="/sector" />}

              <span style={textStyle}>Sectores</span>
            </>
          ),
        },

        {
          key: "CuentasPorCobrar",

          disabled: !menuConfig.clientes.transacciones,

          icon: <CreditCardOutlined style={getIconStyle("#52c41a", menuConfig.clientes.transacciones)} />,

          label: (
            <>
              {menuConfig.clientes.transacciones && <Link to="/cxc" />}

              <span style={textStyle}>Transacciones</span>
            </>
          ),
        },

        {
          key: "OperationType",

          disabled: !menuConfig.clientes.tiposOperacion,

          icon: <TagsOutlined style={getIconStyle("#fa8c16", menuConfig.clientes.tiposOperacion)} />,

          label: (
            <>
              {menuConfig.clientes.tiposOperacion && <Link to="/operationtype" />}

              <span style={textStyle}>Tipos de Operación</span>
            </>
          ),
        },
      ],
    },

    // =======================================================
    // PROVEEDORES
    // =======================================================
    {
      key: "AdminProvider",

      disabled: !menuConfig.proveedores.menuCompleto,

      icon: <TeamOutlined style={getIconStyle("#13c2c2", menuConfig.proveedores.menuCompleto)} />,

      label: <span style={textStyle}>Proveedores</span>,

      children: [
        {
          key: "ProviderRegister",

          disabled: !menuConfig.proveedores.maestroProveedor,

          icon: <WalletOutlined style={getIconStyle("#722ed1", menuConfig.proveedores.maestroProveedor)} />,

          label: (
            <>
              {menuConfig.proveedores.maestroProveedor && <Link to="/supplier" />}

              <span style={textStyle}>Maestro Proveedor</span>
            </>
          ),
        },

        {
          key: "CuentasPorPagar",

          disabled: !menuConfig.proveedores.transacciones,

          icon: <CreditCardOutlined style={getIconStyle("#f5222d", menuConfig.proveedores.transacciones)} />,

          label: (
            <>
              {menuConfig.proveedores.transacciones && <Link to="/cxp" />}

              <span style={textStyle}>Transacciones</span>
            </>
          ),
        },

        {
          key: "ProviderOperationType",

          disabled: !menuConfig.proveedores.tiposOperacion,

          icon: <TagsOutlined style={getIconStyle("#fa8c16", menuConfig.proveedores.tiposOperacion)} />,

          label: (
            <>
              {menuConfig.proveedores.tiposOperacion && <Link to="/provideroperationtype" />}

              <span style={textStyle}>Tipos de Operación</span>
            </>
          ),
        },
      ],
    },

    // =======================================================
    // PRODUCTOS
    // =======================================================
    {
      key: "AdminInventory",

      disabled: !menuConfig.productos.menuCompleto,

      icon: <DatabaseOutlined style={getIconStyle("#fa8c16", menuConfig.productos.menuCompleto)} />,

      label: <span style={textStyle}>Productos</span>,

      children: [
        {
          key: "ProductRegister",

          disabled: !menuConfig.productos.maestroProducto,

          icon: <ShoppingOutlined style={getIconStyle("#52c41a", menuConfig.productos.maestroProducto)} />,

          label: (
            <>
              {menuConfig.productos.maestroProducto && <Link to="/product" />}

              <span style={textStyle}>Maestro Producto</span>
            </>
          ),
        },

        {
          key: "Transaction",

          disabled: !menuConfig.productos.transacciones,

          icon: <FileSyncOutlined style={getIconStyle("#d4b106", menuConfig.productos.transacciones)} />,

          label: (
            <>
              {menuConfig.productos.transacciones && <Link to="/transaction" />}

              <span style={textStyle}>Transacciones</span>
            </>
          ),
        },

        {
          key: "ProductOperationType",

          disabled: !menuConfig.productos.tiposOperacion,

          icon: <TagsOutlined style={getIconStyle("#8b5cf6", menuConfig.productos.tiposOperacion)} />,

          label: (
            <>
              {menuConfig.productos.tiposOperacion && <Link to="/productoperationtype" />}

              <span style={textStyle}>Tipos de Operación</span>
            </>
          ),
        },

        {
          key: "Warehouse",

          disabled: !menuConfig.productos.almacenes,

          icon: <ShopOutlined style={getIconStyle("#2563eb", menuConfig.productos.almacenes)} />,

          label: (
            <>
              {menuConfig.productos.almacenes && <Link to="/warehouse" />}

              <span style={textStyle}>Almacenes</span>
            </>
          ),
        },

        {
          key: "Presentation",

          disabled: !menuConfig.productos.presentacion,

          icon: <BgColorsOutlined style={getIconStyle("#0000FF", menuConfig.productos.presentacion)} />,

          label: (
            <>
              {menuConfig.productos.presentacion && <Link to="/presentation" />}

              <span style={textStyle}>Presentación</span>
            </>
          ),
        },

        {
          key: "Brand",

          disabled: !menuConfig.productos.marcas,

          icon: <CopyrightOutlined style={getIconStyle("#2f54eb", menuConfig.productos.marcas)} />,

          label: (
            <>
              {menuConfig.productos.marcas && <Link to="/brand" />}

              <span style={textStyle}>Marcas</span>
            </>
          ),
        },

        {
          key: "Category",

          disabled: !menuConfig.productos.categorias,

          icon: <FolderOpenOutlined style={getIconStyle("#fa541c", menuConfig.productos.categorias)} />,

          label: (
            <>
              {menuConfig.productos.categorias && <Link to="/Category" />}

              <span style={textStyle}>Categorías</span>
            </>
          ),
        },

        {
          key: "Tax",

          disabled: !menuConfig.productos.impuestos,

          icon: <PercentageOutlined style={getIconStyle("red", menuConfig.productos.impuestos)} />,

          label: (
            <>
              {menuConfig.productos.impuestos && <Link to="/tax" />}

              <span style={textStyle}>Impuestos</span>
            </>
          ),
        },
      ],
    },

    // =======================================================
    // FACTURA
    // =======================================================
    {
      key: "Factura",

      disabled: !menuConfig.facturar,

      icon: <AuditOutlined style={getIconStyle("#f5222d", menuConfig.facturar)} />,

      label: (
        <>
          {menuConfig.facturar && <Link to="/factura" />}

          <span style={textStyle}>Factura</span>
        </>
      ),
    },

    // =======================================================
    // COTIZACION
    // =======================================================
    {
      key: "Invoice",

      disabled: !menuConfig.cotizar,

      icon: <AuditOutlined style={getIconStyle("#f5222d", menuConfig.cotizar)} />,

      label: (
        <>
          {menuConfig.cotizar && <Link to="/invoice" />}

          <span style={textStyle}>{i18n.t("Cotización")}</span>
        </>
      ),
    },

    // =======================================================
    // COMPRAS
    // =======================================================
    {
      key: "Purchase",

      disabled: !menuConfig.compras,

      icon: <ShoppingCartOutlined style={getIconStyle("#10b981", menuConfig.compras)} />,

      label: (
        <>
          {menuConfig.compras && <Link to="/purchase" />}

          <span style={textStyle}>Compras</span>
        </>
      ),
    },

    // =======================================================
    // REPORTES
    // =======================================================
    {
      key: "Reportes",

      disabled: !menuConfig.reportes,

      icon: <BarChartOutlined style={getIconStyle("#722ed1", menuConfig.reportes)} />,

      label: (
        <>
          {menuConfig.reportes && <Link to="/reportes" />}

          <span style={textStyle}>Reportes</span>
        </>
      ),
    },

    // =======================================================
    // IA
    // =======================================================
    {
      key: "SettingsAi",

      disabled: !menuConfig.ai.menuCompleto,

      icon: <SettingOutlined style={getIconStyle("#8c8c8c", menuConfig.ai.menuCompleto)} />,

      label: <span style={textStyle}>{i18n.t("generalSettingsAi")}</span>,

      children: [
        {
          key: "SettingsPageAi",

          disabled: !menuConfig.ai.general,

          label: (
            <>
              {menuConfig.ai.general && <Link to="/settingsAi" />}

              <span style={textStyle}>{i18n.t("settingsAi1")}</span>
            </>
          ),
        },

        {
          key: "RoleAi",

          disabled: !menuConfig.ai.roles,

          label: (
            <>
              {menuConfig.ai.roles && <Link to="/settingsAi2" />}

              <span style={textStyle}>{i18n.t("settingsAi2")}</span>
            </>
          ),
        },
      ],
    },

    // =======================================================
    // CONFIGURACION
    // =======================================================
    {
      key: "Settings",

      disabled: !menuConfig.configuracion.menuCompleto,

      icon: <SettingOutlined style={getIconStyle("#8c8c8c", menuConfig.configuracion.menuCompleto)} />,

      label: <span style={textStyle}>Configuración</span>,

      children: [
        {
          key: "SettingsPage",

          disabled: !menuConfig.configuracion.general,

          label: (
            <>
              {menuConfig.configuracion.general && <Link to="/settings" />}

              <span style={textStyle}>{i18n.t("generalSettings")}</span>
            </>
          ),
        },

        {
          key: "Role",

          disabled: !menuConfig.configuracion.roles,

          label: (
            <>
              {menuConfig.configuracion.roles && <Link to="/role" />}

              <span style={textStyle}>{i18n.t("role")}</span>
            </>
          ),
        },
      ],
    },
  ];

  // =========================================================
  // TOGGLE MENU
  // =========================================================
  const toggle = () => {
    navMenu.collapse();
  };

  // =========================================================
  // LOGOUT
  // =========================================================
  const handleLogout = () => {
    if (msg.logout) {
      msg.logout();
    } else {
      localStorage.removeItem("auth");
      history.push("/login");
    }
  };

  // =========================================================
  // CONTENIDO PERFIL
  // =========================================================
  const profileContent = (
    <div style={{ width: "220px" }}>
      <div style={{ padding: "8px 0", textAlign: "center" }}>
        <Avatar src={photo} size={50} style={{ marginBottom: "8px", border: "2px solid #00a86b" }} />

        <Title level={5} style={{ margin: 0, textTransform: "capitalize" }}>
          {currentUser.name}
        </Title>

        <Text type="secondary" style={{ fontSize: "12px", textTransform: "uppercase" }}>
          {currentUser.tenant}
        </Text>
      </div>

      <Divider style={{ margin: "8px 0" }} />

      <Button type="text" danger icon={<LogoutOutlined />} block onClick={handleLogout} style={{ textAlign: "left", height: "40px" }}>
        Cerrar Sesión
      </Button>
    </div>
  );

  // =========================================================
  // LOGIN
  //
  // IMPORTANTE:
  // ESTE RETURN ESTA DESPUES DE TODOS LOS HOOKS.
  // =========================================================
  if (location.pathname === "/login") {
    return null;
  }

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <>
      <style>{`
        .antd-v4-sider-fix .ant-layout-sider-children {
          display: flex !important;
          flex-direction: column !important;
          height: 100vh !important;
          overflow: hidden !important;
        }

        .menu-scroll-section {
          flex: 1 1 auto;
          overflow-y: hidden !important;
          overflow-x: hidden !important;
          transition: overflow-y 0.2s ease;
        }

        .menu-scroll-section:hover {
          overflow-y: auto !important;
        }

        .menu-scroll-section::-webkit-scrollbar {
          width: 4px;
          background: transparent;
        }

        .menu-scroll-section:hover::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 2px;
        }

        .menu-scroll-section {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
        }

        .menu-scroll-section:hover {
          scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
        }
      `}</style>

      <Sider collapsible collapsed={isNavMenuClose} trigger={null} className="navigation antd-v4-sider-fix" width={250} collapsedWidth={80} theme={currentTheme} style={{ height: "100vh", borderRight: currentTheme === "dark" ? "none" : "1px solid #f0f0f0" }}>
        {/* BOTON COLAPSAR */}
        <Button
          type="default"
          shape="circle"
          icon={isNavMenuClose ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggle}
          style={{
            position: "absolute",
            right: -14,
            top: 20,
            zIndex: 101,
            width: 28,
            height: 28,
            background: currentTheme === "dark" ? "#141414" : "#fff",
            color: currentTheme === "dark" ? "#fff" : "#595959",
            borderColor: currentTheme === "dark" ? "#303030" : "#d9d9d9",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />

        {/* LOGO */}
        <div className="logo" style={{ padding: "10px", textAlign: "center", minHeight: "85px", flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "transparent" }}>
          <div style={{ visibility: isNavMenuClose ? "hidden" : "visible", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
            {config?.logoUrl ? <img src={config.logoUrl} alt="Logo Empresa" style={{ maxHeight: "75px", maxWidth: "100%", objectFit: "contain" }} /> : <img src={logoIcon} alt="Logo Default" style={{ maxHeight: "75px", maxWidth: "100%", objectFit: "contain" }} />}
          </div>
        </div>

        {/* MENU */}
        <div className="menu-scroll-section" style={{ paddingBottom: "16px" }}>
          <Menu mode="inline" theme={currentTheme} style={{ borderRight: 0 }} inlineIndent={12} items={menuItems} />
        </div>

        {/* PERFIL DE USUARIO */}
        <div style={{ padding: "12px 10px", borderTop: `1px solid ${borderColor}`, backgroundColor: "transparent", width: "100%", flexShrink: 0, marginTop: "auto" }}>
          {!isNavMenuClose && (
            <div style={{ textAlign: "center", marginBottom: "6px" }}>
              <Badge dot offset={[-5, 5]}>
                <BellOutlined style={{ fontSize: "18px", cursor: "pointer", color: textColor }} />
              </Badge>
            </div>
          )}

          <Popover content={profileContent} trigger="click" placement="rightBottom" arrow={true}>
            <div style={{ display: "flex", alignItems: "center", padding: isNavMenuClose ? "6px" : "8px 10px", background: "transparent", borderRadius: "8px", justifyContent: isNavMenuClose ? "center" : "flex-start", cursor: "pointer", transition: "all 0.3s ease" }}>
              <Avatar src={photo} size={isNavMenuClose ? 32 : 36} style={{ flexShrink: 0 }} />

              {!isNavMenuClose && (
                <div style={{ marginLeft: "10px", overflow: "hidden", flex: 1 }}>
                  <Text strong style={{ fontSize: "13px", display: "block", textTransform: "capitalize", color: textColor, ...textStyle }}>
                    {currentUser.name}
                  </Text>

                  <Text type="secondary" style={{ fontSize: "11px", display: "block", textTransform: "uppercase", color: textSecondaryColor, ...textStyle }}>
                    {config?.systemName || config?.system_name || currentUser.tenant}
                  </Text>
                </div>
              )}
            </div>
          </Popover>
        </div>
      </Sider>
    </>
  );
}
